import {naturalSort} from '@benchling/js-utils';
import React from 'react';

import {REACTION_FORMATS, EXPORT_DISPLAY_NAME, CHEMICAL_FORMATS} from 'chemistry/ChemistryConstants';
import {
  MarvinChemicalFormat,
  MARVIN_FORMAT_TO_CHEMICAL_FILE_FORMAT,
  CHEMICAL_FILE_FORMAT_TO_MARVIN_FORMAT,
  MarvinCompatibleFileFormat,
} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';

export interface ChemicalSketcherModalInputFieldsProps {
  filename?: string;
  setFilename: (filename: string) => void;
  saveFormat: MarvinChemicalFormat;
  setSaveFormat: (format: MarvinChemicalFormat) => void;
  isReaction: boolean;
}

export default function ChemicalSketcherModalInputFields({
  filename,
  setFilename,
  saveFormat,
  setSaveFormat,
  isReaction,
}: ChemicalSketcherModalInputFieldsProps): JSX.Element {
  const chemicalFileFormat = MARVIN_FORMAT_TO_CHEMICAL_FILE_FORMAT[saveFormat];
  const validFormats = isReaction ? REACTION_FORMATS : CHEMICAL_FORMATS;

  const formatErrorText = !validFormats.includes(chemicalFileFormat)
    ? `${EXPORT_DISPLAY_NAME[chemicalFileFormat]} is not supported for chemical reactions. Please choose a different file format to continue.`
    : '';

  const saveFormatOptions = naturalSort(validFormats).map((item: MarvinCompatibleFileFormat) => (
    <Option
      value={item}
      key={item}
      onSelect={() => {
        setSaveFormat(CHEMICAL_FILE_FORMAT_TO_MARVIN_FORMAT[item]);
      }}
    >
      {EXPORT_DISPLAY_NAME[item]}
    </Option>
  ));

  return (
    <Row>
      <Col sm={6}>
        <FormGroup>
          <FieldLabel label="Name" isRequired={true} />
          <input
            autoFocus={false}
            type="text"
            value={filename}
            placeholder="Untitled chemical"
            onChange={(e) => {
              setFilename(e.target.value);
            }}
            {...getTestProps({component: 'ChemicalSketcherModal', element: 'inputFields', key: 'filename'})}
          />
        </FormGroup>
      </Col>
      <Col sm={6}>
        <FieldLabel label="Format" isRequired={true} />
        <FormGroup>
          <SelectDropdown
            menu={
              <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL} value={saveFormat}>
                {saveFormatOptions}
              </Menu>
            }
            displayName={EXPORT_DISPLAY_NAME[chemicalFileFormat]}
            error={formatErrorText}
          />
        </FormGroup>
      </Col>
    </Row>
  );
}
